import {
  Box,
  CHART_COLOR_NAMES,
  Card,
  PieChart as Chart,
  Grid,
  InfoDot,
  type PieChartTooltipType,
  Text,
  colors,
} from '@factorialco/deprecated-design-system'
import { PieCustomLayerProps } from '@nivo/pie'
import { sum } from 'lodash'

import DraggableNode from 'modules/core/components/DraggableNode'
import useMouseCoords from 'modules/core/hooks/useMouseCoords'
import i18n from 'modules/core/lib/i18n'

import BlankSlate from 'modules/insights/components/BlankSlate'
import ChartTooltip from 'modules/insights/components/ChartTooltip'
import ChartTooltipPercentage from 'modules/insights/components/ChartTooltipPercentage'
import TooltipLegend from 'modules/insights/components/TooltipLegend'

type CenteredMetricProps = {
  label?: string
  customMetric: number | undefined
  hideCustomMetric: boolean
}

const CenteredMetric = ({
  dataWithArc,
  customMetric,
  hideCustomMetric,
  centerX,
  centerY,
  label,
}: PieCustomLayerProps<Record<string, unknown>> & CenteredMetricProps) => {
  const total = sum(dataWithArc.map((datum) => datum.value))
  const percentage = ((customMetric || 0) * 100) / total

  return (
    <>
      {label && (
        <text
          x={centerX}
          y={centerY - 28}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            fontSize: '12px',
            fontWeight: 500,
            color: colors.grey100,
          }}
        >
          {label}
        </text>
      )}
      {!hideCustomMetric && (
        <text
          x={centerX}
          y={centerY}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            fontSize: '36px',
            fontWeight: 500,
            color: colors.grey1000,
          }}
        >
          {`${Math.round(percentage)}%`}
        </text>
      )}
    </>
  )
}

const CenteredMetricLayer = (
  customMetric: number | undefined,
  hideCustomMetric: boolean,
  label?: string
) => {
  const Content = (props: PieCustomLayerProps<Record<string, unknown>>) => (
    <CenteredMetric
      label={label}
      hideCustomMetric={hideCustomMetric}
      customMetric={customMetric}
      {...props}
    />
  )

  return Content
}
type LegendType = 'percentage' | 'count'
type Data = { label: string; value: number }

interface Props {
  title: string
  info: string
  subtitle?: string
  data: Data[]
  customMetric?: number
  hideCustomMetric?: boolean
  legendType?: LegendType
}

const TooltipContainer = ({ data, total }: { total: number; data: Data[] }) => {
  const Tooltip = ({ label, value }: PieChartTooltipType) => {
    const coordinates = useMouseCoords()

    return (
      <DraggableNode coordinates={coordinates}>
        <ChartTooltip title={i18n.t('insights.widgets.shared.headcount')}>
          <TooltipLegend
            color={CHART_COLOR_NAMES[data.findIndex((d) => label === d.label)]!}
            name={label.toString()}
            value={value}
          />
          {<ChartTooltipPercentage percentage={(value / total) * 100} />}
        </ChartTooltip>
      </DraggableNode>
    )
  }

  return Tooltip
}

export const PieChart = ({
  data,
  title,
  info,
  subtitle,
  customMetric,
  hideCustomMetric = false,
  legendType = 'count',
}: Props) => {
  const total = sum(data.map((value) => value.value))
  const centerMetric =
    customMetric || customMetric === 0 || hideCustomMetric
      ? CenteredMetricLayer(customMetric, hideCustomMetric)
      : undefined
  const legendValue = (value: number) => {
    if (legendType == 'count') return value

    return `${Math.round((value * 100) / total)}%`
  }
  return (
    <Card>
      <Card.Title tooltip={info}>{title}</Card.Title>
      <Card.Content>
        {total === 0 && <BlankSlate />}
        {total !== 0 && (
          <Grid variant="fixed">
            <Box gridColSpan={{ xxs: 12 }} justifyContent="center" alignItems="center">
              <Box height="s200" width="s300">
                <Chart
                  margin={{ top: 10, right: 0, bottom: 10, left: 0 }}
                  data={data}
                  tooltip={TooltipContainer({ data, total })}
                  centeredMetricLabel={subtitle}
                  customCenteredMetric={centerMetric}
                />
              </Box>
            </Box>
            <Box gridColSpan={{ xxs: 12 }} justifyContent="center" alignItems="center">
              <Box gap="s24">
                {data.map((datum, index) => (
                  <Box flexDirection="row" key={datum.label} gap="s16">
                    <Box gap="s4" flexDirection="row" flexGrow>
                      <Box flexNoShrink>
                        <InfoDot
                          size="medium"
                          color={CHART_COLOR_NAMES[index % CHART_COLOR_NAMES.length]!}
                        />
                      </Box>
                      <Text.Normal capitalize weight="medium" size="100">
                        {datum.label}
                      </Text.Normal>
                    </Box>
                    <Text.Normal weight="bold" size="100">
                      {legendValue(datum.value)}
                    </Text.Normal>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        )}
      </Card.Content>
    </Card>
  )
}
